import React, { createContext, useContext, useState, useMemo, useEffect } from "react";
import { createTheme } from "@mui/material";

const ThemeContext = createContext();

export const useThemeMode = () => {
  const context = useContext(ThemeContext);
  if (!context) {
    throw new Error("useThemeMode must be used within a ThemeProvider");
  }
  return context;
};

const buildTheme = (mode) =>
  createTheme({
    palette: {
      mode: "light",
      primary: { main: "#52B788", light: "#74C69D", dark: "#2D6A4F" },
      secondary: { main: "#95D5B2", light: "#B7E4C7", dark: "#52B788" },
      background: { default: "#FFFEF9", paper: "#FAF7F2" },
      text: { primary: "#2D6A4F", secondary: "#52B788" },
      error: {
        main: "#f87171",
        light: "#fca5a5",
      },
      success: {
        main: "#52B788",
        light: "#74C69D",
      },
    },
    typography: {
      fontFamily: '"Inter", "Roboto", "Helvetica", "Arial", sans-serif',
      h2: { fontWeight: 900, letterSpacing: "-0.03em" },
      h3: { fontWeight: 800, letterSpacing: "-0.02em" },
      h4: { fontWeight: 700, letterSpacing: "-0.01em" },
      h5: { fontWeight: 700 },
      button: { textTransform: "none", fontWeight: 700 },
    },
    shape: { borderRadius: 16 },
    components: {
      MuiCssBaseline: {
        styleOverrides: {
          body: {
            backgroundImage: "radial-gradient(1400px 700px at 85% -5%, rgba(82,183,136,.05), transparent 65%), radial-gradient(1200px 600px at 5% 115%, rgba(149,213,178,.05), transparent 65%)",
            backgroundAttachment: "fixed",
            backgroundColor: "#FFFEF9",
            color: "#2D6A4F",
          },
          html: {
            backgroundColor: "#FFFEF9",
          },
          "#root": {
            backgroundColor: "#FFFEF9",
            minHeight: "100vh",
          },
          "*:focus-visible": {
            outline: "2px solid #52B788",
            outlineOffset: "2px",
          },
          "*::-webkit-scrollbar": { width: 10 },
          "*::-webkit-scrollbar-track": { background: "#FAF7F2" },
          "*::-webkit-scrollbar-thumb": {
            background: "linear-gradient(180deg, #52B788, #95D5B2)",
            borderRadius: 8,
          },
        },
      },
      MuiPaper: {
        styleOverrides: {
          root: {
            backgroundColor: "rgba(250,247,242,0.9)",
            backdropFilter: "blur(20px)",
            border: "1px solid rgba(82,183,136,0.15)",
          },
        },
      },
      MuiButton: {
        styleOverrides: {
          containedPrimary: {
            background: "linear-gradient(135deg, #52B788 0%, #95D5B2 100%)",
            transition: "transform 0.2s ease, box-shadow 0.2s ease",
            "&:hover": {
              transform: "translateY(-2px)",
              boxShadow: "0 12px 32px rgba(82,183,136,.35)",
            },
          },
        },
      },
      MuiTextField: {
        styleOverrides: {
          root: {
            "& .MuiOutlinedInput-root": {
              "& fieldset": {
                borderColor: "rgba(82, 183, 136, 0.3)",
                borderWidth: "2px",
              },
              "&:hover fieldset": {
                borderColor: "rgba(82, 183, 136, 0.5)",
              },
              "&.Mui-focused fieldset": {
                borderColor: "#52B788",
                boxShadow: "0 0 20px rgba(82, 183, 136, 0.3)",
              },
            },
          },
        },
      },
      MuiSelect: {
        styleOverrides: {
          root: {
            "& .MuiOutlinedInput-notchedOutline": {
              borderColor: "rgba(149, 213, 178, 0.3)",
              borderWidth: "2px",
            },
            "&:hover .MuiOutlinedInput-notchedOutline": {
              borderColor: "rgba(149, 213, 178, 0.5)",
            },
            "&.Mui-focused .MuiOutlinedInput-notchedOutline": {
              borderColor: "#95D5B2",
              boxShadow: "0 0 20px rgba(149, 213, 178, 0.3)",
            },
          },
        },
      },
    },
  });

export const ThemeProvider = ({ children }) => {
  // Load theme from localStorage or default to dark
  const [mode, setMode] = useState(() => {
    const savedMode = localStorage.getItem("themeMode");
    return savedMode || "dark";
  });

  // Save theme to localStorage whenever it changes
  useEffect(() => {
    localStorage.setItem("themeMode", mode);
  }, [mode]);

  const theme = useMemo(() => buildTheme(mode), [mode]);

  const toggleMode = () => {
    setMode((prevMode) => (prevMode === "dark" ? "light" : "dark"));
  };

  const value = {
    mode,
    theme,
    toggleMode,
  };

  return (
    <ThemeContext.Provider value={value}>
      {children}
    </ThemeContext.Provider>
  );
};

